import type { Business, BusinessPatch, CardAccount, DepositPolicy, Service } from '../../api/client'
import { formatLength, formatPrice, noticeSpan, priceOrFree, parsePrice, priceInput } from '../../catalog/format'
import { canTakeCardPayments, cardPaymentsShown } from '../../payments/visibility'

// The rule as the server holds it. Mirrors catalog.DepositFor in the API.
export interface DepositRule {
  policy: DepositPolicy
  amountPence: number | null
  percentage: number | null
}

export const DEPOSIT_POLICIES: { value: DepositPolicy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'fixed', label: 'Fixed amount' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'full', label: 'Full price' },
]

// depositExample is what a client pays to book a service costing pricePence:
// a fixed amount is capped at the price, a percentage is rounded to the
// nearest penny with halves going up, full is the price, and a free service
// never takes a deposit. Zero means no deposit.
export function depositExample(rule: DepositRule, pricePence: number): number {
  if (pricePence <= 0) return 0
  switch (rule.policy) {
    case 'fixed':
      return rule.amountPence === null ? 0 : Math.min(rule.amountPence, pricePence)
    case 'percentage':
      return rule.percentage === null ? 0 : Math.floor((pricePence * rule.percentage + 50) / 100)
    case 'full':
      return pricePence
    default:
      return 0
  }
}

// What is typed in the deposit controls.
export interface DepositDraft {
  policy: DepositPolicy
  amount: string // pounds
  percentage: string
}

export function toDepositDraft(b: Pick<Business, 'deposit_policy' | 'deposit_amount_pence' | 'deposit_percentage'>): DepositDraft {
  return {
    policy: b.deposit_policy,
    amount: b.deposit_amount_pence === null ? '' : priceInput(b.deposit_amount_pence),
    percentage: b.deposit_percentage === null ? '' : String(b.deposit_percentage),
  }
}

export type DepositErrors = Partial<Record<'deposit_amount_pence' | 'deposit_percentage', string>>

// depositRule reads the draft as a rule, or says what is wrong with it.
export function depositRule(d: DepositDraft): { rule: DepositRule; errors: DepositErrors } {
  const rule: DepositRule = { policy: d.policy, amountPence: null, percentage: null }
  const errors: DepositErrors = {}
  if (d.policy === 'fixed') {
    const pence = parsePrice(d.amount)
    if (pence === null || pence < 100) errors.deposit_amount_pence = 'Enter a deposit of at least £1.'
    else rule.amountPence = pence
  }
  if (d.policy === 'percentage') {
    const pct = /^\d{1,3}$/.test(d.percentage.trim()) ? Number(d.percentage.trim()) : 0
    if (pct < 1 || pct > 100) errors.deposit_percentage = 'Choose a percentage between 1 and 100.'
    else rule.percentage = pct
  }
  return { rule, errors }
}

// depositPatch is what to save: the policy and, for fixed or percentage, its
// number. The other numbers are left as they are on the server.
export function depositPatch(rule: DepositRule): BusinessPatch {
  const patch: BusinessPatch = { deposit_policy: rule.policy }
  if (rule.policy === 'fixed') patch.deposit_amount_pence = rule.amountPence
  if (rule.policy === 'percentage') patch.deposit_percentage = rule.percentage
  return patch
}

// depositChangeable is whether the draft differs from what is saved.
export function depositChanged(d: DepositDraft, b: Business): boolean {
  const saved = toDepositDraft(b)
  if (d.policy !== saved.policy) return true
  if (d.policy === 'fixed') return d.amount.trim() !== saved.amount
  if (d.policy === 'percentage') return d.percentage.trim() !== saved.percentage
  return false
}

// depositChoiceDisabled is whether a choice cannot be picked. "None" always
// can, so a deposit that has paused can still be switched off. Every other
// choice needs card payments ready.
export function depositChoiceDisabled(policy: DepositPolicy, account: CardAccount | null): boolean {
  return policy !== 'none' && !canTakeCardPayments(account)
}

// cardSetupLinked is whether the "set up card payments first" note can link to
// the Card payments panel, which only exists where card payments are offered.
export function cardSetupLinked(account: CardAccount | null): boolean {
  return cardPaymentsShown(account)
}

// depositPaused is whether a deposit is set but not being taken, because card
// payments stopped being ready. Online bookings are then taken without one.
export function depositPaused(b: Pick<Business, 'deposit_policy' | 'deposits_active'>): boolean {
  return b.deposit_policy !== 'none' && !b.deposits_active
}

export const DEPOSIT_PAUSED_WARNING =
  'Deposits paused — card payments need attention. Online bookings are taken without a deposit.'

// DepositExample is what the example line is about: a treatment and its price.
export interface DepositExample {
  name: string
  price_pence: number
}

// exampleService is the treatment the example line is about: the first
// service offered online with a length that costs something, at its shortest
// such length. A service with several lengths is named with it ("Signature ·
// 60 min").
export function exampleService(services: Service[]): DepositExample | null {
  for (const s of services) {
    if (!s.bookable_online) continue
    const length = s.lengths.find((l) => l.price_pence > 0)
    if (!length) continue
    const name = s.lengths.length > 1 ? `${s.name} · ${formatLength(length.duration_minutes)}` : s.name
    return { name, price_pence: length.price_pence }
  }
  return null
}

// exampleLine: "Relaxing Massage (£55): clients pay £20 to book".
export function exampleLine(rule: DepositRule, example: DepositExample | null): string | null {
  if (!example || rule.policy === 'none') return null
  const deposit = depositExample(rule, example.price_pence)
  if (deposit === 0) return null
  return `${example.name} (${priceOrFree(example.price_pence)}): clients pay ${formatPrice(deposit)} to book`
}

// refundNote: what a client is promised when they cancel on their own.
export function refundNote(noticeHours: number): string {
  return `Clients can cancel online for a full refund up to ${noticeSpan(noticeHours)} before.`
}
